import { useState, useEffect, createContext, useContext } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, HelpCircle, ExternalLink } from 'lucide-react';
import { getPageHelp } from '../../lib/page-help';

interface HelpDrawerContextType {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const HelpDrawerContext = createContext<HelpDrawerContextType>({
  isOpen: false, open: () => {}, close: () => {}, toggle: () => {},
});

export function useHelpDrawer() {
  return useContext(HelpDrawerContext);
}

export function HelpDrawerProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <HelpDrawerContext.Provider value={{
      isOpen,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      toggle: () => setIsOpen(v => !v),
    }}>
      {children}
      <HelpDrawerPanel />
    </HelpDrawerContext.Provider>
  );
}

function HelpDrawerPanel() {
  const { isOpen, close } = useHelpDrawer();
  const { t } = useTranslation('pages');
  const location = useLocation();

  const pageHelp = getPageHelp(location.pathname, t);

  if (!isOpen) return null;

  return (
    <>
      <div
        onClick={close}
        style={{
          position: 'fixed', inset: 0, zIndex: 9990,
          background: 'rgba(0,0,0,0.3)',
          animation: 'helpOverlayIn 0.2s ease-out',
        }}
      />
      <style>{`
        @keyframes helpOverlayIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes helpSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 340, maxWidth: '90vw',
        zIndex: 9991,
        background: '#111', color: '#fff',
        boxShadow: '-4px 0 30px rgba(0,0,0,0.4)',
        display: 'flex', flexDirection: 'column',
        animation: 'helpSlideIn 0.25s ease-out',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HelpCircle size={18} style={{ color: '#B8860B' }} />
            <span style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 14, fontWeight: 700,
            }}>
              {t('helpDrawer.quickHelp')}
            </span>
          </div>
          <button
            onClick={close}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', padding: 4 }}
            aria-label="Close help"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {pageHelp ? (
            <>
              <p style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 11, fontWeight: 600, color: '#B8860B',
                textTransform: 'uppercase', letterSpacing: '0.08em',
                margin: '0 0 12px',
              }}>
                {pageHelp.title} — {t('helpDrawer.tips')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {pageHelp.tips.map((tip) => (
                  <div key={tip.label} style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 10, padding: '12px 14px',
                    borderLeft: '3px solid #B8860B',
                  }}>
                    <p style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 12, fontWeight: 700, color: '#fff',
                      margin: '0 0 4px',
                    }}>
                      {tip.label}
                    </p>
                    <p style={{
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                      fontSize: 11, color: 'rgba(255,255,255,0.6)',
                      lineHeight: 1.5, margin: 0,
                    }}>
                      {tip.text}
                    </p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 12, color: 'rgba(255,255,255,0.5)',
              textAlign: 'center', marginTop: 40,
            }}>
              {t('helpDrawer.noTips')}
            </p>
          )}

          {/* General quick links */}
          <div style={{
            marginTop: 24, paddingTop: 16,
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}>
            <p style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.3)',
              textTransform: 'uppercase', letterSpacing: '0.08em',
              margin: '0 0 10px',
            }}>
              {t('helpDrawer.quickLinks')}
            </p>
            {[
              { to: '/help', label: t('helpDrawer.links.fullGuide'), desc: t('helpDrawer.links.fullGuideDesc') },
              { to: '/faq', label: t('helpDrawer.links.faq'), desc: t('helpDrawer.links.faqDesc') },
              { to: '/contact', label: t('helpDrawer.links.contact'), desc: t('helpDrawer.links.contactDesc') },
              { to: '/cookies#manage', label: t('helpDrawer.links.cookies'), desc: t('helpDrawer.links.cookiesDesc') },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                onClick={close}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 8,
                  textDecoration: 'none', color: '#fff',
                  marginBottom: 4,
                  background: 'rgba(255,255,255,0.03)',
                }}
              >
                <div style={{ flex: 1 }}>
                  <p style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 12, fontWeight: 600, margin: 0,
                  }}>
                    {link.label}
                  </p>
                  <p style={{
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: 10, color: 'rgba(255,255,255,0.4)',
                    margin: '2px 0 0',
                  }}>
                    {link.desc}
                  </p>
                </div>
                <ExternalLink size={12} style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
              </Link>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.08)',
          textAlign: 'center',
        }}>
          <Link
            to="/help"
            onClick={close}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#B8860B', color: '#fff', border: 'none',
              borderRadius: 8, padding: '8px 20px', cursor: 'pointer',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 12, fontWeight: 700, textDecoration: 'none',
            }}
          >
            {t('helpDrawer.viewFullGuide')} <ExternalLink size={12} />
          </Link>
        </div>
      </div>
    </>
  );
}
